import { Brain } from '@phosphor-icons/react';
import { METRIC_KEYS, createEasyFeedback, createProfessionalFeedback, getCognitiveLabels, type MetricKey, type MetricScores } from '../../shared/data/cognitiveEngine';
import { readAllReports } from '../../shared/data/cognitiveReportsStorage';

function averageMetrics(reports: { metrics: MetricScores }[]): MetricScores {
  return METRIC_KEYS.reduce((result, key) => ({
    ...result,
    [key]: Math.round(reports.reduce((sum, report) => sum + (report.metrics?.[key] || 0), 0) / reports.length),
  }), {} as MetricScores);
}

function CognitiveIntegratedReport({ language }: { language: string }) {
  const isKorean = language === 'ko';
  const reports = readAllReports();
  if (!reports.length) {
    return (
      <section className="cognitive-integrated-report cognitive-integrated-empty">
        <div>
          <Brain weight="duotone" />
          <span>
            <small>{isKorean ? '인지 통합 리포트' : 'COGNITIVE INTEGRATED REPORT'}</small>
            <h2>{isKorean ? '인지학습을 완료하면 변화가 누적됩니다' : 'Complete a cognitive activity to build your profile'}</h2>
            <p>{isKorean ? '색상 순서 기억 등 각 활동의 공통 5개 지표를 모아 통합 변화와 소견을 제공합니다.' : 'Five shared metrics from each activity build your combined changes and interpretation.'}</p>
          </span>
        </div>
        <em>{isKorean ? '학습 기록 없음' : 'NO ACTIVITY RECORDS'}</em>
      </section>
    );
  }
  const latest = reports[0];
  const metrics = averageMetrics(reports);
  const previousReports = reports.slice(1);
  const previous = previousReports.length ? averageMetrics(previousReports) : null;
  const change = previous
    ? METRIC_KEYS.map((key) => ({ key, value: metrics[key] - previous[key] })).sort((a, b) => Math.abs(b.value) - Math.abs(a.value))[0]
    : null;
  const labels = getCognitiveLabels(language);
  const feedback = createProfessionalFeedback(metrics, ['integrated', reports.length, latest.id], [], language, 'integrated');
  const easyFeedback = createEasyFeedback(metrics, language);
  return (
    <section className="cognitive-integrated-report">
      <header>
        <div>
          <Brain weight="duotone" />
          <span>
            <small>{isKorean ? '인지 통합 리포트' : 'COGNITIVE INTEGRATED REPORT'}</small>
            <h2>{isKorean ? `총 ${reports.length}회 학습 기록의 통합 변화` : `Combined change across ${reports.length} sessions`}</h2>
          </span>
        </div>
        <em>{isKorean ? '공통 5개 지표' : 'FIVE SHARED METRICS'}</em>
      </header>
      <div className="integrated-metric-grid">
        {METRIC_KEYS.map((key: MetricKey) => (
          <div key={key}>
            <span><strong>{labels[key]}</strong><b>{isKorean ? `${metrics[key]}점` : `${metrics[key]} points`}</b></span>
            <i><b style={{ width: `${metrics[key]}%` }} /></i>
          </div>
        ))}
      </div>
      {change && (
        <p className="integrated-change">
          {isKorean ? `이전 통합 기록 대비 ${labels[change.key]} ${change.value >= 0 ? '+' : ''}${change.value}점` : `${labels[change.key]} ${change.value >= 0 ? '+' : ''}${change.value} points vs. previous records`}
        </p>
      )}
      <div className="integrated-opinion integrated-easy-opinion">
        <span>{isKorean ? '나를 위한 쉬운 안내' : 'EASY GUIDE'}</span>
        <h3>{easyFeedback.title}</h3>
        <p>{easyFeedback.body}</p>
        <ul>
          <li>{easyFeedback.strength}</li>
          <li>{easyFeedback.support}</li>
        </ul>
      </div>
      <div className="integrated-opinion">
        <span>{isKorean ? '전문가용 통합 소견' : 'PROFESSIONAL INTERPRETATION'}</span>
        <p>{feedback.narrative}</p>
        <small>{isKorean ? '활동별 원점수를 단순 합산하지 않고, 공통 지표로 환산한 통합 학습 소견입니다.' : 'This combined learning interpretation converts each activity into five shared metrics instead of simply adding raw scores.'}</small>
      </div>
    </section>
  );
}

export default CognitiveIntegratedReport;
